import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import {
  AutocompleteSearch,
  ButtonUI,
  ModalUI,
  Selector,
  showNotification,
  Tag,
  useQueryFetch
} from 'sonata-react-components';
import { setSonataSession } from 'sonata-static-storage/lib/helpers/session.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { SelectorSkeleton } from '@/components/common/SelectorSkeleton/SelectorSkeleton';
import {
  getPreferredUsers,
  getSearchUsers,
  maskCustomUserAccess,
  maskUserAccess,
  restoreCustomUser,
  restoreMaskUser
} from '@/services/masquerade.service';
import { useMenu } from '../Menu.context';

import type { SessionInterface } from 'sonata-static-storage/lib/schemas/session/session.schema';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';
import type { UserRoleInterface } from 'sonata-static-storage/lib/schemas/user/userRole.schema';

import './ChangeUserModal.styles.scss';

export type Listeners = {
  onBackToUser: () => void;
  onMasqueradeUserChange: (accessId: string) => void;
  onViewAsChange: (userId: string) => void;
};

type SuggestionUser = {
  email: string;
  id: string;
  label: string;
  name?: string;
  value: UserInterface;
};

type Props = {
  id: string;
  session: SessionInterface;
  user: UserInterface;
  userRoles: UserRoleInterface[];
} & Listeners;

type State = {
  selectedPreferredUser: UserInterface | null;
  selectedUser: UserInterface | null;
};

export function ChangeUserModal({
  id,
  onBackToUser,
  onMasqueradeUserChange,
  onViewAsChange,
  session,
  user,
  userRoles
}: Props) {
  const { formatMessage } = useIntl();
  const modalRef = useRef<React.ElementRef<typeof ModalUI>>(null);
  const { showChangeUserModal } = useMenu();
  const {
    sonataAppUrl
    // @ts-ignore
  } = taptap.config;
  const hasViewAsPermission = checkUserResourcePermissions('masqueradeViewAs', user, userRoles);

  const [{ selectedPreferredUser, selectedUser }, setState] = useState<State>({
    selectedPreferredUser: null,
    selectedUser: null
  });

  useEffect(() => {
    modalRef?.current?.openModal();
  }, [modalRef]);

  const preferredUsersToken = {};
  const {
    data: preferredUsers,
    isFetching: isFetchingPreferredUsers,
    isLoading: isLoadingPreferredUsers
  } = useQueryFetch('masquerade-preferred-users', async () => getPreferredUsers(preferredUsersToken), {
    enabled: true
  });

  const getSelectedUserId = () => {
    if (selectedPreferredUser) {
      return selectedPreferredUser.id || (selectedPreferredUser._id as string);
    }
    if (selectedUser) {
      return selectedUser.id || (selectedUser._id as string);
    }
    return null;
  };

  const maskUserAccessMutation = useMutation({
    mutationFn: (data: { from: string; to: string }) => maskUserAccess(data),
    mutationKey: ['mask-user-access'],
    onError: (err: any) => {
      if (err && err.response && err.response.status === 403) {
        // unauthorized, lost session or band cookie, redirect to Sonata
        window.location.replace(sonataAppUrl);
      } else {
        throw err;
      }
    },
    onSuccess: (userId: string) => {
      showNotification(
        'success',
        formatMessage(
          {
            defaultMessage: 'Changes successfully applied. You are currently switched to "{targetUser}" user.',
            id: 'changeUserModal.changeUserSuccessNotification'
          },
          {
            targetUser: selectedPreferredUser?.name || selectedUser?.name
          }
        )
      );
      if (onMasqueradeUserChange) {
        onMasqueradeUserChange(userId);
      } else {
        window.location.href = `${window.location.origin}/#!/masquedlogin/${userId}`;
      }
    }
  });

  const maskViewAsMutation = useMutation({
    mutationFn: (userId: string) => maskCustomUserAccess(userId),
    mutationKey: ['mask-view-as-user-access'],
    onError: (err: any) => {
      if (err && err.response && err.response.status === 403) {
        // unauthorized, lost session or band cookie, redirect to Sonata
        window.location.replace(sonataAppUrl);
      } else {
        throw err;
      }
    },
    onSuccess: () => {
      const selectedUserId = getSelectedUserId();
      showNotification(
        'success',
        formatMessage(
          {
            defaultMessage: session.userFrom
              ? 'Changes successfully applied. You are currently switched to "{currentUser}" and Viewing as "{targetUser}"'
              : 'Changes successfully applied. You are currently switched to "{targetUser}" user.',
            id: session.userFrom
              ? 'changeUserModal.changeAndViewAsSuccessNotification'
              : 'changeUserModal.viewAsSuccessNotification'
          },
          {
            currentUser: user.name,
            targetUser: selectedPreferredUser?.name || selectedUser?.name
          }
        )
      );
      onViewAsChange ? onViewAsChange(selectedUserId!) : location.reload();
    }
  });

  const backToMyUserMutation = useMutation({
    mutationFn: () => restoreMaskUser(session),
    mutationKey: ['back-to-my-user'],
    onSuccess: newSession => {
      setSonataSession(newSession);
      if ((session as Record<string, any>).zendesk) {
        window.location.replace(session.zendeskUrl);
        return;
      }
      if (onBackToUser) {
        onBackToUser();
        return;
      }
      window.location.replace(sonataAppUrl);
    }
  });

  const backViewAsMyUserMutation = useMutation({
    mutationFn: () => restoreCustomUser(),
    mutationKey: ['back-to-view-as-my-user'],
    onSuccess: newSession => {
      setSonataSession(newSession);
      if ((session as Record<string, any>).zendesk) {
        window.location.replace(session.zendeskUrl);
        return;
      }
      if (onBackToUser) {
        onBackToUser();
        return;
      }
      window.location.replace(sonataAppUrl);
    }
  });

  const preferredUsersOptions =
    preferredUsers?.map(user => ({
      id: user.id || (user._id as string),
      label: user.name || user.email
    })) || [];

  const handlePreferredUserChange = (option: { id: string }) => {
    const preferredUser = preferredUsers?.find(user => (user.id || (user._id as string)) === option.id);
    setState(prev => ({ ...prev, selectedPreferredUser: preferredUser || null }));
  };

  const handleChangeUserToggled = async () => {
    const selectedUserId = getSelectedUserId();
    if (!selectedUserId) {
      return;
    }
    maskUserAccessMutation.mutate({
      from: session.userFrom || user.id || (user._id as string),
      to: selectedUserId
    });
  };

  const handleViewAsToggled = () => {
    const selectedUserId = getSelectedUserId();
    if (!selectedUserId) {
      return;
    }
    maskViewAsMutation.mutate(selectedUserId);
  };

  const fetchData = async (searchText: string): Promise<SuggestionUser[]> => {
    const users = await getSearchUsers(searchText, {});

    return users.map(user => {
      return {
        email: user.email,
        id: user.id || (user._id as string),
        label: user.name || user.email,
        name: user.name,
        value: user
      };
    });
  };

  const handleCloseToggled = () => {
    showChangeUserModal('hide');
  };

  const handleBackToUserToggled = () => {
    backToMyUserMutation.mutate();
  };

  const handleBackToViewAsMyUserToggled = () => {
    backViewAsMyUserMutation.mutate();
  };

  const handleUserSelectionChange = (suggestion: { value: UserInterface } | null) => {
    setState(prev => ({ ...prev, selectedUser: suggestion?.value || null }));
  };

  const backToMyUser = Boolean(user.customUser || session.userFrom);
  const backToViewAsMyUser = Boolean(user.customUser);

  const currentChangeUserStatues = [];
  if (session.userFrom) {
    currentChangeUserStatues.push({
      type: 'switchTo',
      userName: user.name ?? user.email
    });
  }
  if (user.customUser) {
    currentChangeUserStatues.push({
      type: 'viewAs',
      userName: user.customUser?.name ?? user.customUser?.email
    });
  }

  const buttonsLoadingState = {
    back: backToMyUserMutation.isLoading,
    changeUser: maskUserAccessMutation.isLoading,
    viewAs: maskViewAsMutation.isLoading || backViewAsMyUserMutation.isLoading
  };

  const someUserSelected = Boolean(selectedPreferredUser) || Boolean(selectedUser);

  const buttonsDisabledState = {
    back: buttonsLoadingState.changeUser || buttonsLoadingState.viewAs,
    changeUser: !someUserSelected || buttonsLoadingState.back || buttonsLoadingState.viewAs,
    viewAs: !backToViewAsMyUser && (!someUserSelected || buttonsLoadingState.back || buttonsLoadingState.changeUser)
  };

  return (
    <ModalUI
      className="changeUserModal"
      closeModalCallback={handleCloseToggled}
      description={formatMessage({
        defaultMessage: 'Switch to another user or view as someone else',
        id: 'changeUserModal.description'
      })}
      id={id}
      leftButton={
        <ButtonUI
          {...(backToMyUser && {
            icon: 'fa-solid fa-arrow-left'
          })}
          disabled={buttonsDisabledState.back}
          id={`${id}-${backToMyUser ? 'backToMyUserButton' : 'cancelButton'}`}
          loading={buttonsLoadingState.back}
          onClick={backToMyUser ? handleBackToUserToggled : handleCloseToggled}
          shape="squareRounded"
          text={formatMessage({
            defaultMessage: backToMyUser ? 'Back to my user' : 'Cancel',
            id: backToMyUser ? 'changeUserModal.backToMyUserButton' : 'changeUserModal.cancelButton'
          })}
          type="tertiary"
        />
      }
      ref={modalRef}
      rightButton={
        <div className="changeUserModal__rightButtonsContainer">
          {hasViewAsPermission && (
            <ButtonUI
              disabled={buttonsDisabledState.viewAs}
              icon={backToViewAsMyUser ? 'fa-solid fa-arrow-left' : 'fa-regular fa-eye'}
              id={`${id}-viewAsUserButton`}
              loading={buttonsLoadingState.viewAs}
              onClick={backToViewAsMyUser ? handleBackToViewAsMyUserToggled : handleViewAsToggled}
              shape="squareRounded"
              text={formatMessage({
                defaultMessage: backToViewAsMyUser ? 'View as my user' : 'View as this user',
                id: `changeUserModal.${backToViewAsMyUser ? 'viewAsMyUserButton' : 'viewAsThisUserButton'}`
              })}
              type="secondary"
            />
          )}
          <ButtonUI
            disabled={buttonsDisabledState.changeUser}
            icon="fa-solid fa-shuffle"
            id={`${id}-changeUserButton`}
            loading={buttonsLoadingState.changeUser}
            onClick={handleChangeUserToggled}
            shape="squareRounded"
            text={formatMessage({
              defaultMessage: 'Switch to this user',
              id: 'changeUserModal.changeToThisUserButton'
            })}
            type="primary"
          />
        </div>
      }
      title={formatMessage({
        defaultMessage: 'Change user',
        id: 'changeUserModal.title'
      })}
      width="613px"
    >
      <div className="changeUserModal__container">
        {isFetchingPreferredUsers || isLoadingPreferredUsers ? (
          <SelectorSkeleton />
        ) : (
          <Selector
            label={formatMessage({
              defaultMessage: 'Select preferred user',
              id: 'changeUserModal.preferredUserLabel'
            })}
            onChange={handlePreferredUserChange}
            options={preferredUsersOptions || []}
            placeholder={formatMessage({
              defaultMessage: 'Select user',
              id: 'changeUserModal.preferredUserPlaceholder'
            })}
            type="simple"
          />
        )}
        <div className="changeUserModal__contentSeparator">
          <span className="changeUserModal__contentSeparatorLine" />
          <span className="changeUserModal__contentSeparatorLabel">
            {formatMessage({
              defaultMessage: 'OR',
              id: 'changeUserModal.separatorLabel'
            })}
          </span>
          <span className="changeUserModal__contentSeparatorLine" />
        </div>
        <AutocompleteSearch<SuggestionUser>
          fetchData={fetchData}
          id="autocompleteSearchFetch"
          label={formatMessage({
            defaultMessage: 'Search user',
            id: 'changeUserModal.userSearchLabel'
          })}
          onSelectionChange={handleUserSelectionChange}
          placeholder={formatMessage({
            defaultMessage: 'Type the name, email...',
            id: 'changeUserModal.userSearchPlaceholder'
          })}
          showInputLens
        >
          {suggestions =>
            suggestions.map(suggestion => (
              <AutocompleteSearch.Suggestion id={suggestion.id} key={suggestion.id}>
                <div className="changeUserModal__suggestionContainer">
                  <span className="changeUserModal__suggestionName">{suggestion.name}</span>
                  <span className="changeUserModal__suggestionEmail">{suggestion.email}</span>
                </div>
              </AutocompleteSearch.Suggestion>
            ))
          }
        </AutocompleteSearch>
        {currentChangeUserStatues.length > 0 ? (
          <div className="changeUserModal__changeUserStatusContainer">
            {currentChangeUserStatues.map((status, index) => (
              <div className="changeUserModal__changeUserStatus" key={index}>
                <Tag id={`${id}-changeUserStatus-${status.type}`} tiny>
                  {status.userName}
                </Tag>
                <span className="changeUserModal__changeUserStatusDescription">
                  {formatMessage(
                    {
                      defaultMessage: 'You are currently {content}',
                      id: 'changeUserModal.changeUserStatusDescription'
                    },
                    {
                      content: (
                        <span className="changeUserModal__changeUserStatusLabelHighlight">
                          {formatMessage({
                            defaultMessage: status.type === 'viewAs' ? 'viewing as this user' : 'switched to this user',
                            id:
                              status.type === 'viewAs'
                                ? 'changeUserModal.changeUserStatusViewingAsThisUser'
                                : 'changeUserModal.changeUserStatusSwitchThisUser'
                          })}
                        </span>
                      )
                    }
                  )}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </ModalUI>
  );
}
